import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostBinding,
  ElementRef,
  ViewChild,
  signal,
} from '@angular/core';
import {BehaviorSubject, map, Subject} from 'rxjs';
import {filter, skip, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {Location} from '@angular/common';

import * as ThemeOptions from '../../theme/interfaces/options';
import {IThemeSevice} from '../../theme/interfaces/theme.interface';
import {zAppDevComponentStatus} from '../component-status';
import {CF_COMPONENT} from '@framework/rule-engine/directives/condition-formatting.directive';
import {zAppDevBaseComponent} from '../BaseComponent/base.component';
import {transformStyleToObject} from '@framework/common';
import {ContextService} from '../../../@core/services/Context.service';
import {PermissionService} from '../../security/services/permission.service';
import {Navigation, NavigationEnd, Router} from '@angular/router';
import {MenuItem} from "primeng/api";
import {zAppDevMenuItem} from "@framework/components/MenuControl/menu-item";
import {ListboxClickEvent} from "primeng/listbox";
import {AuthService} from "@services/Auth.service";
import {BreadcrumbGraphService} from "@framework/components/Breadcrumb/BreadcrumbGraph.service";
import {Title} from "@angular/platform-browser";
import {TieredMenu, TieredMenuModule} from "primeng/tieredmenu";
import {CommonModule} from '@angular/common';
import {BreadcrumbModule} from 'primeng/breadcrumb';
import {RippleModule} from 'primeng/ripple';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {zAppDevIconismModule} from '@framework/components/Iconism/iconism.module';

@Component({
  selector: 'zapp-breadcrumb',
  styleUrls: ['./Breadcrumb.component.less'],
  templateUrl: './Breadcrumb.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, BreadcrumbModule, TieredMenuModule, zAppDevIconismModule, RippleModule, RouterLink, RouterLinkActive],
  providers: [{ provide: CF_COMPONENT, useExisting: zAppBreadcrumbComponent }],
})
export class zAppBreadcrumbComponent extends zAppDevBaseComponent {
  protected destroy$ = new Subject<void>();

  protected options: ThemeOptions.BreadcrumbThemeOptions;

  @Input() variation: string = 'Standard';

  @Input() status: zAppDevComponentStatus = 'global';

  @Input() class: string = '';

  private _menuItems: zAppDevMenuItem[];
  get menuItems(): any {
    return this._menuItems;
  }
  @Input() set menuItems(value: any) {
    this._menuItems = value;
  }

  @Input() showIcons: boolean = true; // Show the menu page icons and the home icon in the breadcrumb

  @Input() showCurrentPage: boolean = true; // Show the current page in the breadcrumb if it is not in the menu

  @ViewChild('op') overlayPanel: TieredMenu;

  breadcrumbItems = signal<MenuItem[]>([]);  // the list of items in the breadcrumb
  breadcrumbHome = signal<MenuItem>(null);  // the home item in the breadcrumb
  breadcrumbCurrentItems: MenuItem[] = []; // the list of children in the breadcrumb item that is in the overlay
  breadcrumbCurrentSelected: MenuItem;     // the selected item of the overlay items

  // override to apply style classes on the root element
  @HostBinding('class')
  get hostClasses(): string {
    return this.hostClassesArray().concat(this.styleClassesArray()).join(' ');
  }

  @HostBinding('class.hidden')
  get isHidden() {
    return this.hidden === true;
  }

  constructor(
      protected readonly translate: TranslateService,
      protected themeService: IThemeSevice,
      private router: Router,
      private contextService: ContextService,
      private titleService: Title,
      private location: Location,
      private graphService: BreadcrumbGraphService,
      protected readonly permissionsService: PermissionService,
      protected readonly authService: AuthService,
      private permissionService: PermissionService,
      protected elementRef: ElementRef) {
    super(elementRef);
    this.options = themeService.getBreadcrumbThemeOptions();
  }

  breadcrumbClicked(item: MenuItem, event: MouseEvent, op: TieredMenu) {
    if (!item.items) {
      return;
    }
    this.breadcrumbCurrentItems = item.items
        .filter(x => x.visible);
    this.breadcrumbCurrentSelected = this.breadcrumbCurrentItems.find(x => x.routerLink === this.router.url);
    if (this.breadcrumbCurrentItems.length === 0) {
      return;
    }
    op.toggle(event);
  }

  breadcrumbItemClicked(event: MouseEvent, url: string) {
    // If middle click or control click
    if (event.button === 1 || event.ctrlKey) {
      const fullUrl = this.location.prepareExternalUrl(url);
      window.open(fullUrl, '_blank');
      this.overlayPanel.hide();
      return;
    }
    // If left click
    if (event.button === 0) {
      this.router.navigateByUrl(url)
          .then(_ => this.overlayPanel.hide());
      return;
    }
  }

  updateBreadcrumbs(url: string) {
    const menuSearch = this.searchMenu(item => item.route === url);
    if (menuSearch) {
      // If we are in the home page, we only show the home breadcrumb
      if (menuSearch[menuSearch.length - 1].isHeader) {
        this.breadcrumbItems.set([]);
        return;
      }
      // Otherwise we remove the first element because it is the virtual root
      this.breadcrumbItems.set(menuSearch.slice(1).map(item => this.convertMenuItem(item)));
    } else {
      // If current route not found in the menu, we search the graph
      const graphPath = this.graphService.search(this.getPath(url));
      // Get the first element of graphPath and search the menu for it
      const graphMenuSearch = this.searchMenu(item => item.route === graphPath[0]);
      // If found, we add it to the breadcrumb; otherwise leave breadcrumbs empty
      if (graphMenuSearch) {
        this.breadcrumbItems.set(graphMenuSearch.slice(1).map(item => this.convertMenuItem(item)));
      } else {
        this.breadcrumbItems.set([]);
      }
    }
  }

  updateHomeBreadcrumb() {
    const homeSearch = this.searchMenu(item => item.isHeader);
    if (homeSearch) {
      this.breadcrumbHome.set(this.convertMenuItem(homeSearch[homeSearch.length - 1]));
    } else {
      this.breadcrumbHome.set({ url: '/' });
    }
  }

  updateLastBreadcrumb() {
    // Only works if the option is enabled
    if (!this.showCurrentPage) {
      return;
    }
    // Get the page title
    const pageTitle = this.titleService.getTitle();
    const titleWithoutSuffix = pageTitle.split(' | ')[0];
    // Remove all items from the breadcrumb with the lastBreadcrumb flag set
    let items = this.breadcrumbItems().filter(item => !item.lastBreadcrumb);
    // Add the last breadcrumb if the current page is not in the menu
    if (this.searchMenu(item => item.route === this.router.url) == null) {
      items.push({ label: titleWithoutSuffix, visible: true, lastBreadcrumb: true });
    }
    this.breadcrumbItems.set(items);
  }

  /**
   * Converts a zAppDevMenuItem into a MenuItem.
   */
  convertMenuItem(item: zAppDevMenuItem): MenuItem {
    return {
      label: item.label ? item.label() : '',
      url: item.url,
      routerLink: item.route,
      icon: item.icon,
      visible: this.hasAccess(item),
      items: item.children?.map(child => this.convertMenuItem(child))
    };
  }

  /**
   * Find the given menu item in the input list of items.
   */
  findMenuItem(item: MenuItem): zAppDevMenuItem {
    return this.searchMenu(menuItem => menuItem.route === item.routerLink)[0];
  }

  /**
   * Search the menu recursively for an item that matches the given predicate.
   * Optionally specify the root item to start the search from (used for recursion).
   * Returns the tree path to the matched item from the top to bottom.
   * This method also exists in the search modal component.
   */
  searchMenu(predicate: (item: zAppDevMenuItem) => boolean, root?: zAppDevMenuItem): zAppDevMenuItem[] | null {
    if (!root) {
      root = { name: undefined, children: this.menuItems };
    }
    if (predicate(root)) {
      return [root];
    }
    if (!root.children) {
      return null;
    }
    for (let child of root.children) {
      const path = this.searchMenu(predicate, child);
      if (path) {
        return [root, ...path];
      }
    }
    return null;
  }

  /**
   * Copied from MenuControlService (but changed childs to children).
   */
  private hasAccess(item: zAppDevMenuItem): boolean {
    let visible = false;

    if (this.authService.isLoggedOut()) {
      visible = item.authorization.allowAnonymous;
    }

    if (this.authService.isLoggedIn()) {
      if (item.authorization.allowAuthenticated) {
        visible = true;
      } else {
        visible = this.permissionsService.hasPermission(item.authorization.permissions);
      }
    }

    if (item.children != null && item.children.length > 0) {
      const children = item.children.map((child) => this.hasAccess(child));

      if (visible) {
        visible = children.every(child => child == false);
      } else {
        visible = children.some(child => child == true);
      }
    }

    return visible;
  }

  /**
   * Convert a Navigation object into a string path.
   */
  getPath(navigation: Navigation | string): string {
    let urlString: string;

    // Type guard to check if the input is a Navigation object
    if (typeof navigation !== 'string' && navigation.finalUrl) {
      urlString = navigation.finalUrl.toString();
    } else if (typeof navigation === 'string') {
      urlString = navigation;
    } else {
      alert('BreadcrumbComponent: Invalid navigation object');
      return ''; // Return an empty string if the input does not match expected types
    }

    // Process the URL string
    return urlString.split('/')
        .filter(segment => segment && segment !== '/')
        .slice(0, 2)
        .join('/');
  }

  /**
   * Convert a Navigation object into a full URL.
   */
  getFullPath(navigation: Navigation): string {
    return navigation.finalUrl.toString();
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Update the breadcrumb items when language changes
    this.translate
        .onLangChange
        .pipe()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateHomeBreadcrumb();
          this.updateBreadcrumbs(this.router.url);
          setTimeout(() => {
            this.updateLastBreadcrumb();
          }, 1);
        });

    // Update the breadcrumb items when permissions change
    this.permissionService.permissions
        .pipe(skip(1))
        .pipe(takeUntil(this.destroy$))
        .subscribe((context) => {
          this.updateBreadcrumbs(this.router.url);
        });

    // Graph heuristic creation service
    this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      // Get previous and after URL
      let afterURL = this.router.getCurrentNavigation().finalUrl.toString();
      let previousURL = this.router.getCurrentNavigation().previousNavigation.finalUrl.toString();
      // Check if previous and after URL exist (just in case)
      if (!previousURL || !afterURL) {
        return;
      }
      // If afterURL is already in the menu, we don't need to do anything
      const afterSearch = this.searchMenu(item => item.route === event.url);
      if (afterSearch != null) {
        return;
      }
      // If previousURL is not in the menu, we convert it to a path
      if (!this.searchMenu(item => item.route === previousURL)) {
        previousURL = this.getPath(previousURL);
      }
      // Convert afterURL to a path
      afterURL = this.getPath(afterURL);
      // Add the edge to the graph
      this.graphService.addEdge(previousURL, afterURL);
      // Update breadcrumbs
      this.updateBreadcrumbs(event.url);
      // Update last breadcrumb
      setTimeout(() => {
        this.updateLastBreadcrumb();
      }, 1);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
